import React, { useState } from "react"
import isUsernameAvailable from "../util/fetchApiHelpers/isUsernameAvailable"
import updateUser from "../util/fetchApiHelpers/updateUser"
import * as SocketIOClient from "socket.io-client"
import type { User } from "../types"

export interface UserSettingsModalProps {
	user: User
	onClose: () => void
	onUserHasUpdated: () => void
	socket: SocketIOClient.Socket
}

export default function UserSettingsModal(props: UserSettingsModalProps) {
	let { name: originalName } = props.user

	let [name, setName] = useState<string>(originalName)
	let [nameError, setNameError] = useState<string | null>(null)

	let [avatar, setAvatar] = useState<string>(props.user.avatar)
	let [avatarError] = useState<string | null>(null)

	async function handleNameChange(event: React.ChangeEvent<HTMLInputElement>) {
		let newName = event.target.value.split(" ").join("-")

		setName(newName)
		setNameError(null)

		if (newName.length === 0) {
			setNameError(
				"Noname is a great rapper, but unfortunately not a great option for you on Jam",
			)
		} else {
			let isNameAvailable = await isUsernameAvailable(newName)

			if (originalName.toLowerCase() === newName.toLowerCase()) {
				isNameAvailable = true
			}

			if (!isNameAvailable) {
				setNameError(
					"Username is already taken, probably by someone just as awesome as you",
				)
			}
		}
	}

	async function handleAvatarChange(
		event: React.ChangeEvent<HTMLInputElement>,
	) {
		let newAvatar = event.target.value
		setAvatar(newAvatar)
	}

	async function handleSubmit(event: React.FormEvent) {
		event.preventDefault()

		if (nameError !== null || avatarError !== null) {
			// TODO: This should have better error handling
			return
		}

		await updateUser({
			name,
			avatar,
		})

		props.onUserHasUpdated()
		props.onClose()

		props.socket.emit("tellEveryoneToRemoveOldUserFromMentions", originalName)
		props.socket.emit("updateConnectedUser")
	}

	return (
		<div className="jam--wrapper">
			<div className="jam--modal-wrapper">
				<img
					onClick={props.onClose}
					className="jam--close"
					src="https://danigrant.github.io/img/close.svg"
				/>
				<a href="https://jam.dev" target="_blank" rel="noopener noreferrer">
					<img
						className="jam--graphic"
						src="https://strawberry-jam-manufacturers.github.io/img/logo.png"
					/>
				</a>
				<h1>{`Jam is a place you can be whoever you want to be ✨`}</h1>
				<h2>{`Yes you can, ${name}`}</h2>
				<form onSubmit={handleSubmit}>
					<label>Profile Picture</label>
					<img className="jam--avatar-preview" src={avatar} />
					<input type="text" value={avatar} onChange={handleAvatarChange} />
					{avatarError !== null && (
						<div className="jam--validation-error-message">{avatarError}</div>
					)}
					<label>Username</label>
					<input type="name" value={name} onChange={handleNameChange} />
					{nameError !== null && (
						<div className="jam--validation-error-message">{nameError}</div>
					)}
					{/* I commented email input out because there's no endpoint for checking if email is unique yet */}
					{/* <label>Email</label>
									<input type="email" value={email} onChange={this.handleEmailChange} />
									{emailValidationError && <div className="jam--validation-error-message">{emailValidationError}</div>}                         */}
					<button type="submit">Transform Yourself</button>
				</form>
			</div>
			<div onClick={props.onClose} className="jam--overlay"></div>
			<style>{`
				.jam--overlay {
					background: rgba(0, 0, 0, 0.5);
					position: fixed;
					z-index: 1000001;
					width: 100%;
					height: 100vh;
					top: 0;
					left: 0;
				}

				.jam--modal-wrapper {
					display: flex;
					flex-direction: column;
					width: 630px;
					max-width: 90%;
					justify-content: center;
					height: fit-content;
					position: fixed;
					top: 0;
					bottom: 0;
					left: 0;
					right: 0;
					margin: auto;
					z-index: 1000002;
					background: #ffffff;
					box-sizing: border-box;
					box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
					border-radius: 2px;
					overflow: hidden;
					font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
						"Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans",
						"Helvetica Neue", sans-serif;
					color: #2a3632;
					padding: 3em 3.5em;
				}

				.jam--avatar-preview {
					width: 55px;
					height: 55px;
					border-radius: 50%;
				}

				a {
					color: #9194ff;
					cursor: pointer;
					text-decoration: none;
				}

				h1 {
					font-size: 2em;
				}

				h2 {
					font-weight: 400;
					color: rgba(42, 54, 50, 0.5);
					margin-top: 0;
					font-size: 18px;
				}

				form {
					display: flex;
					flex-direction: column;
					max-width: 100%;
				}

				input {
					width: 100%;
					outline: none;
					border: none;
					font-size: inherit;
					color: inherit;
					padding: 1em;
					margin-top: 1em;
					background: #f4f4f4;
					box-sizing: border-box;
					border-radius: 2px;
					border: 1px solid #e2e2e2;
					margin-bottom: 2em;
				}

				label {
					font-size: 14px;
					line-height: 20px;
				}

				button {
					width: 100%;
					height: 50px;
					display: flex;
					flex-direction: row;
					align-items: center;
					justify-content: center;
					background: #3776fd;
					border-radius: 5px;
					border: none;
					outline: none;
					color: white;
					font-weight: 500;
					font-size: 1em;
					margin-bottom: 1rem;
					cursor: pointer;
				}

				.jam--validation-error-message {
					background: rgba(255, 64, 112, 0.15);
					padding: 0 10px;
					margin-bottom: 2em;
					color: #ff4070;
					font-weight: 500;
					border: 1px solid #ff4070;
					border-radius: 2px;
				}

				.jam--graphic {
					width: 60px;
				}

				.jam--close {
					position: absolute;
					top: 1em;
					right: 1em;
					cursor: pointer;
				}
			`}</style>
		</div>
	)
}
